/**
 * The set of UI-Router state parameters that are scoped to a specific domain
 * and must be stripped when the user switches to a different domain.
 *
 * Extend this set when a new domain-specific URL param is introduced.
 */
export const DOMAIN_SCOPED_PARAMS: ReadonlySet<string> = new Set<string>([
    /**
     * Opens the page-details dialog on the dashboard for the given page.
     * The page ID is domain-specific — the same numeric ID does not exist
     * (or refers to a different page) on the target domain.
     */
    'page_id',

    /**
     * Anchors the page-details panel to a specific section
     * (e.g. 'page-details-section-qa', 'page-details-section-accessibility').
     */
    'target',

    /**
     * Selects a sub-section within page-details
     * (e.g. 'brokenlinks', 'misspellings', 'accessibility').
     */
    'subComponent',
]);

/**
 * Returns a shallow copy of `params` with all DOMAIN_SCOPED_PARAMS removed.
 *
 * This is a pure function — it does not mutate the input object.
 *
 * @param params - The current UI-Router state params to sanitise.
 * @returns A new object with domain-scoped keys removed.
 */
export function stripDomainScopedParams (
    params: Record<string, unknown>,
): Record<string, unknown> {
    const result: Record<string, unknown> = { ...params };
    for (const key of DOMAIN_SCOPED_PARAMS) {
        delete result[key];
    }
    return result;
}

/**
 * Builds the navigation params object for a domain switch, given the current
 * UI-Router state params and the new domain ID.
 *
 * This function:
 * 1. Strips all domain-scoped params (see DOMAIN_SCOPED_PARAMS)
 * 2. Sets `domainId` to the provided new domain ID
 * 3. Does NOT touch `domain_group` — the caller must handle this param
 *    explicitly (set it for domain-group switches, delete it for plain domains)
 *
 * @param currentParams - The current UI-Router state params (stateService.params).
 * @param newDomainId - The numeric ID of the domain being switched to.
 * @returns A new param object safe to pass to stateService.go().
 */
export function buildDomainSwitchParams (
    currentParams: Record<string, unknown>,
    newDomainId: number,
): Record<string, unknown> {
    const params = stripDomainScopedParams(currentParams);
    params['domainId'] = newDomainId;
    return params;
}

/**
 * The UI-Router navigation options to use when switching domains.
 *
 * - `reload: 'base.customer.domain'` — reloads the domain state and all
 *   descendant states, ensuring data bound to the domain context is refreshed.
 * - `inherit: false` — prevents unspecified params from being inherited from
 *   currently active parent states, ensuring a clean param set.
 */
export const DOMAIN_SWITCH_OPTIONS = {
    reload: 'base.customer.domain',
    inherit: false,
} as const;
